/**
 * Auth store —— token + 当前玩家信息。
 *
 * Action 边界:
 * - `bootstrap()`: 启动时调用一次, 如果 localStorage 里有 token, 拉一遍 /me
 *   验证 token 仍有效; 失败则清状态。
 * - `login()` / `register()`: 调 API → 写 token+me; 调用方决定后续 navigate。
 * - `logout()`: 清状态。WS 断开由 ws store 监听 token 变化处理 (M2+)。
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'

import { login as apiLogin, register as apiRegister, type AuthResponse } from '@/api/auth'
import { getMe } from '@/api/me'
import { ApiError } from '@/api/http'

// /me 响应形状, 与 app/profile/routes.py:MeResponse 对齐。
export interface MeResponse {
  pid:          string
  username:     string
  name:         string
  display_name: string
  avatar:       string
  money:        number
  is_admin:     boolean
  room_id?:     string | null
  status:       'online' | 'playing' | 'offline'
  is_spectator: boolean
}

const TOKEN_KEY = 'poker_token'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem(TOKEN_KEY))
  const me    = ref<MeResponse | null>(null)

  function setToken(value: string | null): void {
    token.value = value
    if (value) localStorage.setItem(TOKEN_KEY, value)
    else       localStorage.removeItem(TOKEN_KEY)
  }

  function setMe(value: MeResponse | null): void {
    me.value = value
  }

  /** AuthResponse 不含 display_name/avatar 等字段, 需要紧接一次 /me 拉全。*/
  async function _hydrateFromAuthResp(resp: AuthResponse): Promise<void> {
    setToken(resp.token)
    try {
      const full = await getMe()
      setMe(full)
    } catch {
      // /me 失败不致命, 用 AuthResponse 的精简信息凑合
      setMe({
        pid:          resp.pid,
        username:     '',
        name:         resp.name,
        display_name: '',
        avatar:       '',
        money:        resp.money,
        is_admin:     resp.is_admin,
        room_id:      null,
        status:       'online',
        is_spectator: false,
      })
    }
  }

  async function login(username: string, password: string): Promise<void> {
    const resp = await apiLogin({ username, password })
    await _hydrateFromAuthResp(resp)
  }

  async function register(username: string, name: string, password: string, inviteCode?: string): Promise<void> {
    const req = inviteCode
      ? { username, name, password, invite_code: inviteCode }
      : { username, name, password }
    const resp = await apiRegister(req)
    await _hydrateFromAuthResp(resp)
  }

  /** 启动时调用; 若 token 存在但失效, 清状态。返回是否恢复成功。*/
  async function bootstrap(): Promise<boolean> {
    if (!token.value) return false
    try {
      const full = await getMe()
      setMe(full)
      return true
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // token 失效
        logout()
        return false
      }
      // 网络/其他错误, 保持 token 但 me 为空, 让 UI 自行处理
      console.warn('[auth] bootstrap failed:', err)
      return false
    }
  }

  function logout(): void {
    setToken(null)
    setMe(null)
  }

  return {
    token, me,
    setToken, setMe,
    login, register, bootstrap, logout,
  }
})
